// contexts/HeaderContext.tsx
import { IconProps } from '@/components/icons/types'
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

interface HeaderRightTitle {
  text: string
  icon: React.FC<IconProps>
}

export interface HeaderTitleNavigation {
  current: 'application' | 'workspace'
  target: string
}

interface HeaderStateContent {
  coBrandingImageKey?: string
  title1: string
  title2?: string
  titleUrl?: string
  titleNavigation?: HeaderTitleNavigation
  privacyUrl?: string
  usePrivacyPolicyDialog?: boolean
  privacyPolicyApplicationId?: number
  rightTitle?: HeaderRightTitle
}

interface HeaderContextType extends HeaderStateContent {
  setHeaderSnapshot: (ownerId: symbol, snapshot: HeaderStateContent) => void
  clearHeaderSnapshot: (ownerId: symbol) => void
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined)

export const DEFAULT_HEADER_STATE: HeaderStateContent = {
  coBrandingImageKey: undefined,
  title1: 'Data Modelling Platform',
  title2: undefined,
  titleUrl: undefined,
  titleNavigation: undefined,
  privacyUrl: undefined,
  usePrivacyPolicyDialog: false,
  privacyPolicyApplicationId: undefined,
  rightTitle: undefined,
}

export function HeaderProvider({ children }: { children: ReactNode }) {
  const [headerContent, setHeaderContent] = useState<HeaderStateContent>(DEFAULT_HEADER_STATE)
  const headerOwnersRef = useRef<symbol[]>([])
  const headerSnapshotsRef = useRef<Map<symbol, HeaderStateContent>>(new Map())

  const applyHeaderContent = useCallback((nextContent: HeaderStateContent) => {
    setHeaderContent((currentContent) => {
      return areHeaderSnapshotsEqual(currentContent, nextContent) ? currentContent : nextContent
    })
  }, [])

  const setHeaderSnapshot = useCallback(
    (ownerId: symbol, snapshot: HeaderStateContent) => {
      headerSnapshotsRef.current.set(ownerId, snapshot)
      if (!headerOwnersRef.current.includes(ownerId)) {
        headerOwnersRef.current = [...headerOwnersRef.current, ownerId]
      }

      if (headerOwnersRef.current.at(-1) === ownerId) {
        applyHeaderContent(snapshot)
      }
    },
    [applyHeaderContent]
  )

  const clearHeaderSnapshot = useCallback(
    (ownerId: symbol) => {
      headerSnapshotsRef.current.delete(ownerId)
      headerOwnersRef.current = headerOwnersRef.current.filter((currentOwnerId) => currentOwnerId !== ownerId)

      const nextOwnerId = headerOwnersRef.current.at(-1)
      if (nextOwnerId) {
        const nextSnapshot = headerSnapshotsRef.current.get(nextOwnerId)
        if (nextSnapshot) {
          applyHeaderContent(nextSnapshot)
        }
      }
    },
    [applyHeaderContent]
  )

  const value = useMemo(
    () => ({
      ...headerContent,
      setHeaderSnapshot,
      clearHeaderSnapshot,
    }),
    [clearHeaderSnapshot, headerContent, setHeaderSnapshot]
  )

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>
}

export function useHeader() {
  const context = useContext(HeaderContext)
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider')
  }
  return context
}

export function useSetHeaderContext(params: HeaderStateContent) {
  const { setHeaderSnapshot, clearHeaderSnapshot } = useHeader()
  const ownerIdRef = useRef(Symbol('header-owner'))
  const {
    coBrandingImageKey,
    title1,
    title2,
    titleUrl,
    titleNavigation,
    privacyUrl,
    usePrivacyPolicyDialog,
    privacyPolicyApplicationId,
    rightTitle: { text, icon } = {},
  } = params

  const snapshot = useMemo(
    () => ({
      coBrandingImageKey,
      title1,
      title2,
      titleUrl,
      titleNavigation: titleNavigation?.target
        ? {
            current: titleNavigation.current,
            target: titleNavigation.target,
          }
        : undefined,
      privacyUrl,
      usePrivacyPolicyDialog,
      privacyPolicyApplicationId,
      rightTitle: text && icon ? { text, icon } : undefined,
    }),
    [
      coBrandingImageKey,
      icon,
      privacyUrl,
      privacyPolicyApplicationId,
      text,
      title1,
      title2,
      titleNavigation,
      titleUrl,
      usePrivacyPolicyDialog,
    ]
  )

  useLayoutEffect(() => {
    const ownerId = ownerIdRef.current
    setHeaderSnapshot(ownerId, snapshot)

    return () => clearHeaderSnapshot(ownerId)
  }, [clearHeaderSnapshot, setHeaderSnapshot, snapshot])
}

function areHeaderSnapshotsEqual(current: HeaderStateContent, next: HeaderStateContent) {
  return (
    current.coBrandingImageKey === next.coBrandingImageKey &&
    current.title1 === next.title1 &&
    current.title2 === next.title2 &&
    current.titleUrl === next.titleUrl &&
    current.privacyUrl === next.privacyUrl &&
    current.usePrivacyPolicyDialog === next.usePrivacyPolicyDialog &&
    current.privacyPolicyApplicationId === next.privacyPolicyApplicationId &&
    current.titleNavigation?.current === next.titleNavigation?.current &&
    current.titleNavigation?.target === next.titleNavigation?.target &&
    current.rightTitle?.text === next.rightTitle?.text &&
    current.rightTitle?.icon === next.rightTitle?.icon
  )
}
